// client/src/pages/Admin/ProjectManager/utils/projectSearch.ts
/**
 * PROJECT SEARCH
 * 
 * This file contains search-related utilities for the ProjectManager.
 * 
 * @module ProjectManager/Utils/Search
 */

import { Project } from '../types/project.types';
import { formatCategoryLabel } from './projectHelpers';
import { MIN_SEARCH_CHARS, MAX_SEARCH_SUGGESTIONS } from '../constants/project.constants';

// ============================================
// SEARCH FUNCTIONS
// ============================================

/**
 * Highlight matching text in a string
 * @param text - Text to highlight
 * @param query - Search query
 * @returns HTML string with highlighted matches
 * 
 * @example
 * highlightMatch('Project Title', 'title') // 'Project <mark>Title</mark>'
 */
export const highlightMatch = (text: string, query: string): string => {
    if (!text || !query) return text;
    try {
        const regex = new RegExp(
            `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
            'gi'
        );
        return text.replace(regex, '<mark>$1</mark>');
    } catch {
        return text;
    }
};

/**
 * Check if a project matches a search query and return match fields
 * @param project - Project to check
 * @param query - Search query
 * @returns Object with matches and match fields
 * 
 * @example
 * resourceMatchesSearch(project, 'education')
 * // { matches: true, matchFields: ['title', 'category'] }
 */
export const projectMatchesSearch = (
    project: Project,
    query: string
): { matches: boolean; matchFields: string[] } => {
    if (!query || !project) {
        return { matches: false, matchFields: [] };
    }

    const search = query.toLowerCase().trim();
    if (!search) {
        return { matches: true, matchFields: [] };
    }

    const matchFields: string[] = [];

    // Check title
    if ((project.title ?? '').toLowerCase().includes(search)) {
        matchFields.push('title');
    }

    // Check subtitle
    if ((project.subtitle ?? '').toLowerCase().includes(search)) {
        matchFields.push('subtitle');
    }

    // Check description
    if ((project.description ?? '').toLowerCase().includes(search)) {
        matchFields.push('description');
    }

    // Check full description
    if ((project.fullDescription ?? '').toLowerCase().includes(search)) {
        matchFields.push('fullDescription');
    }

    // Check tags
    if ((project.tags ?? []).some((tag: string) => 
        tag.toLowerCase().includes(search)
    )) {
        matchFields.push('tags');
    }

    // Check category
    const categoryLabel = formatCategoryLabel(project.category);
    if (categoryLabel.toLowerCase().includes(search)) {
        matchFields.push('category');
    }

    // Check regions
    if ((project.regions ?? []).some((region: string) => 
        region.toLowerCase().includes(search)
    )) {
        matchFields.push('regions');
    }

    // Check districts
    if ((project.districts ?? []).some((district: string) => 
        district.toLowerCase().includes(search)
    )) {
        matchFields.push('districts');
    }

    return {
        matches: matchFields.length > 0,
        matchFields
    };
};

/**
 * Generate search suggestions based on project data
 * @param projects - Array of projects
 * @param searchTerm - Current search term
 * @returns Array of suggestion strings
 * 
 * @example
 * generateSearchSuggestions(projects, 'educ')
 * // ['Education', 'Tag: youth']
 */
export const generateSearchSuggestions = (
    projects: Project[],
    searchTerm: string
): string[] => {
    if (!searchTerm || searchTerm.length < MIN_SEARCH_CHARS) {
        return [];
    }

    if (!Array.isArray(projects)) {
        return [];
    }

    const search = searchTerm.toLowerCase().trim();
    const suggestions = new Set<string>();

    projects.forEach(project => {
        // Title suggestions
        if ((project.title ?? '').toLowerCase().includes(search)) {
            suggestions.add(project.title || '');
        }

        // Category suggestions
        const category = formatCategoryLabel(project.category);
        if (category.toLowerCase().includes(search)) {
            suggestions.add(`Category: ${category}`);
        }

        // Tag suggestions
        (project.tags ?? []).forEach((tag: string) => {
            if (tag.toLowerCase().includes(search)) {
                suggestions.add(`Tag: ${tag}`);
            }
        });

        // Region suggestions
        (project.regions ?? []).forEach((region: string) => {
            if (region.toLowerCase().includes(search)) {
                suggestions.add(`Region: ${region}`);
            }
        });

        // District suggestions
        (project.districts ?? []).forEach((district: string) => {
            if (district.toLowerCase().includes(search)) {
                suggestions.add(`District: ${district}`);
            }
        });
    });

    return Array.from(suggestions).slice(0, MAX_SEARCH_SUGGESTIONS);
};

/**
 * Get search result count for a query
 * @param projects - Array of projects
 * @param query - Search query
 * @returns Number of matching projects
 */
export const getSearchResultCount = (
    projects: Project[],
    query: string
): number => {
    if (!query || !Array.isArray(projects)) return 0;
    const search = query.toLowerCase().trim();
    if (!search) return projects.length;
    
    return projects.filter(project => 
        projectMatchesSearch(project, search).matches
    ).length;
};

/**
 * Get highlighted search text for a field
 * @param project - Project to check
 * @param field - Field to highlight
 * @param query - Search query
 * @returns Highlighted text or original text
 */
export const getHighlightedField = (
    project: Project,
    field: keyof Project,
    query: string
): string => {
    if (!project || !query) return '';
    const value = project[field];
    if (typeof value !== 'string') return '';
    return highlightMatch(value, query);
};